import React from 'react';
import { Button, Checkbox, FormControlLabel, Grid, TextField, Typography} from '@material-ui/core';

import { CutoutModel, cutoutModelInitial } from '../../../../Models/CutoutModel';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { theme } from '../../../../theme';
import { ClientContext } from '../../ClientContext';
import { EditAction } from '../../../../shared/enum/EditAction';
import { OrganogramModel } from '../../../../Models/OrganogramModel';
import { HomeContext } from '../../../Home/HomeContext';
import { ClientCutoutModalTabs } from '../../enums/ClientCutoutModalTabs';
import { CutoutModalMenu } from '../components/CutoutModalMenu';
import { CutoutModalPages } from '../components/CutoutModalPages';
import { ClientErrorContext } from '../../ClientErrorContext';

interface ICutoutModalProps {
    isOpen: boolean,
    setIsOpen: (open: boolean) => void,
    isInEditMode: boolean,
    setIsInEditMode: (open: boolean) => void,
    currentCutout: CutoutModel,
    setCurrentCutout: (cutout: CutoutModel) => void
    actionIndex: number,
    setActionIndex: (index: number) => void
}

export function CutoutModal({
    isOpen, setIsOpen,
    isInEditMode, setIsInEditMode,
    currentCutout, setCurrentCutout,
    actionIndex, setActionIndex }: ICutoutModalProps) {

    const homeContext = React.useContext(HomeContext); 
    const clientContext = React.useContext(ClientContext);
    const clientErrorContext = React.useContext(ClientErrorContext);

    const [isSaveAsNewModalOpen, setIsSaveAsNewModalOpen] = React.useState(false);
    const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = React.useState(false);

    const [descriptionNotDefinedError, setDescriptionNotDefinedError] = React.useState(false);

    const [cutoutPageTab, setCutoutPageTab] = React.useState<ClientCutoutModalTabs>(ClientCutoutModalTabs.DEFAULT);

    const clearAndCloseCutoutModal = () => {
        setIsOpen(false);
        setCurrentCutout({...cutoutModelInitial, vehicles: []});
        setActionIndex(-1);
        setIsInEditMode(false);
        setDescriptionNotDefinedError(false);
        setIsSaveAsNewModalOpen(false);
        setIsSaveChangesModalOpen(false);

        clientErrorContext.setOrganogramNotSelectedInCutoutError(false);

        setCutoutPageTab(ClientCutoutModalTabs.DEFAULT);
    };

    const hasError = () => {
        const descriptionNotDefinedError = !currentCutout.description;
       
        const organogramList = JSON.parse(JSON.stringify(clientContext.cutoutOrganogram));
        const organogramNotSelected = organogramList.filter((o: OrganogramModel) => o.editAction == EditAction.SELECTED).length == 0;

        setDescriptionNotDefinedError(descriptionNotDefinedError);
        clientErrorContext.setOrganogramNotSelectedInCutoutError(organogramNotSelected);

        return descriptionNotDefinedError || organogramNotSelected;
    };

    const isNotDuplicated = () => {
        const limit : number = (isInEditMode && !isSaveAsNewModalOpen) ? 1 : 0;
        const description = currentCutout.description;

        const isDuplicated = clientContext.cutouts.filter(c => c.description.toLowerCase() == description.toLowerCase()).length > limit;

        if(isDuplicated)
            homeContext.showWarning('Já existe um recorte com este nome. Altere o nome do recorte e tente novamente.');

        return !isDuplicated;
    };

    const handleSaveCutout = () => {
        if (!hasError() && isNotDuplicated()) {
            const cutouts = JSON.parse(JSON.stringify(clientContext.cutouts));

            const selectedOrganogram = JSON.parse(JSON.stringify(clientContext.cutoutOrganogram)).filter((org: OrganogramModel)=> org.editAction == EditAction.SELECTED);
            currentCutout.organograms = selectedOrganogram;

            if (isInEditMode && !isSaveAsNewModalOpen) {
                cutouts[actionIndex] = currentCutout;

                if (cutouts[actionIndex].editAction != EditAction.INSERT)
                    cutouts[actionIndex].editAction = EditAction.UPDATE;

                clientContext.setCutouts(cutouts);
                setIsInEditMode(false);
            }
            else
                clientContext.setCutouts([...cutouts, { ...currentCutout, editAction: EditAction.INSERT}]);
            
            clearAndCloseCutoutModal();
        }
    };

    const handleSaveAsNewOrSaveChanges = (saveAsNew: boolean) => {
        setIsOpen(false);

        if (saveAsNew && !hasError())
            setIsSaveAsNewModalOpen(true);
        else if (!hasError() && isNotDuplicated())
            setIsSaveChangesModalOpen(true);
        else 
            setIsOpen(true);
    };

    const getCutoutHeader = () => {
        return (
            <Grid 
                item xs={12}
                container
                direction="row"
                justify="center"
                spacing={(isSaveAsNewModalOpen ? 0 : 3)}>
                { getCutoutName() }
                { getCutoutCouncurrent() }
            </Grid>
        );
    };

    const getCutoutName = () => {
        return (
            <Grid item xs={12} md={(isSaveAsNewModalOpen ? 12 : 6)}>
                <TextField
                    id="name"
                    value={currentCutout.description}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => 
                        setCurrentCutout({ ...currentCutout, description: event.target.value })}
                    fullWidth
                    label="Nome do recorte"
                    variant="outlined"
                    inputProps={{ maxLength: 300 }}
                    style={{ marginBottom: theme.spacing(2) }}
                    error={descriptionNotDefinedError && !currentCutout.description}
                    helperText={descriptionNotDefinedError && !currentCutout.description ? 'Campo obrigatório' : ''}
                />
            </Grid>
        );
    };

    const getCutoutCouncurrent = () => {
        return (
            <Grid item xs={12} md={(isSaveAsNewModalOpen ? 12 : 6)}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currentCutout.councurrent}
                            onChange={() => setCurrentCutout({ ...currentCutout, councurrent: !currentCutout.councurrent })}
                            name="Compara concorrente"
                            color="primary" />
                    }
                    label="Compara concorrente" />
            </Grid>
        );
    };

    const cutoutModalButtons = () => {
        return (
            <Grid 
                container
                spacing={2}
                direction='row'
                justify='space-between'>
                <Grid item>
                    <Button 
                        onClick={clearAndCloseCutoutModal} 
                        size="large">
                        Voltar
                    </Button>
                </Grid>
                <Grid item>
                    <Button 
                        onClick={() => handleSaveAsNewOrSaveChanges(true)} 
                        variant="outlined" 
                        color="primary"
                        size="large"
                        disableElevation>
                        Salvar como novo recorte
                    </Button>
                </Grid>
                <Grid item>
                    <Button 
                        onClick={() => handleSaveAsNewOrSaveChanges(false)} 
                        variant="contained" 
                        color="primary"
                        size="large"
                        disableElevation>
                        Salvar Alterações
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const getCutoutModalChildren = () => {
        return (
            <Grid container>

                { getCutoutHeader() }
                
                <Grid item xs={12} style={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }}>
                    <Typography variant='body2'>
                        Selecione os itens presentes nos recortes.
                    </Typography>
                </Grid>

                <CutoutModalMenu
                    currentTab={ cutoutPageTab }
                    setCurrentTab={setCutoutPageTab} />

                <CutoutModalPages
                    currentTab={ cutoutPageTab }
                    editMode={isInEditMode}
                    currentCutout={ currentCutout }
                    setCurrentCutout={ setCurrentCutout } />
            </Grid>
        );
    };

    const getSaveAsNewCutoutChildren = () => {
        return (
            <Grid container>
                { getCutoutHeader() }
            </Grid>
        );
    };
    
    return (
        <>
            <BasicConfirmDialog
                title={isInEditMode ? 'Edição de recorte' : 'Cadastro de Recorte'}
                textFirstButton="Descartar"
                handleFirstButton={ clearAndCloseCutoutModal }
                textSecondButton={isInEditMode ? 'Salvar Alterações' : 'Salvar Recorte'}
                handleSecondButton={ handleSaveCutout }
                open={ isOpen }
                children={ getCutoutModalChildren() }
                customButtons={isInEditMode ? cutoutModalButtons() : undefined}
                cssProperties={{ 
                    minWidth: '800px',
                    maxWidth: '800px',
                    padding: '1rem',
                    colorFirstButton: theme.palette.primary.main,
                    justifyButtons: 'flex-end',
                    titleVariant: 'h4',
                    descriptionVariant: 'body1'
                }}
            />

            <BasicConfirmDialog
                title="Salvar novo recorte"
                description="Insira um novo nome para este recorte."
                textFirstButton="Cancelar"
                handleFirstButton={() => {
                    setIsOpen(true);
                    setIsSaveAsNewModalOpen(false);
                }}
                textSecondButton="Salvar novo recorte"
                handleSecondButton={ handleSaveCutout }
                open={ isSaveAsNewModalOpen }
                children={ getSaveAsNewCutoutChildren() }
                cssProperties={{ justifyButtons: 'flex-end' }} />
            
            <BasicConfirmDialog
                title="Salvar alterações?"
                description={ 'Ao alterar esse recorte o dashboard será afetado para todas as classificações, inclusive matérias retroativas.' +
                        `Deseja salvar as alterações do recorte ${currentCutout.description}?` }
                textFirstButton="Voltar"
                handleFirstButton={() => {
                    setIsOpen(true);
                    setIsSaveChangesModalOpen(false);
                }}
                textSecondButton="Salvar"
                handleSecondButton={ handleSaveCutout }
                cssProperties={{ justifyButtons: 'flex-end' }}
                open={ isSaveChangesModalOpen } />
        </>
    );
}